'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';

import SelectInput from '@/components/common/ui/input/SelectInput';
import StockSingle from '@/components/common/ui/single-item/StockSingle';
import { StyledStockList } from '@/components/common/ygen/sections/stock-list/stock-list.style';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import reactHtmlParser from 'react-html-parser';

const StockList = ({ data, params, productData, productBrandData }) => {
	const [getProductData, setProductData] = useState(productData); // Store scroll data in state

	// Category data is all products whose parent_product_id is 0 (top-level categories)
	let categoryData =
		getProductData?.filter(f => f?.product_data?.parent_product_id === 0) ||
		[];
	const [getCategoryData, setCategoryData] = useState(categoryData); // Store scroll data in state
	// Sort productBrandData by sort_order (0 to 12) before setting state
	const sortedBrandProductData = Array.isArray(productBrandData)
		? [...productBrandData].sort(
				(a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999),
			)
		: [];
	const [getBrandProductData, setBrandProductData] = useState(
		sortedBrandProductData,
	); // Store scroll data in state
	const [getTypeDataAll, setTypeDataAll] = useState(0); // Store scroll data in state
	const [getBrandDataAll, setBrandDataAll] = useState(0); // Store scroll data in state
	const pathName = usePathname();
	const searchParams = useSearchParams();
	const router = useRouter();
	const slug = pathName?.split('/ready-stock/')[1]; // Get the part after '/ready-stock/'
	useEffect(() => {
		if (categoryData) {
			setCategoryData(categoryData);
		}
		if (productData) {
			setProductData(productData);
		}
		if (productBrandData) {
			setBrandProductData(productBrandData);
		}
	}, []);

	// Separate effect for URL parameter handling
	useEffect(() => {
		const brandSlug = searchParams.get('brand');
		const categoryParam = searchParams.get('category');

		if (brandSlug && getBrandProductData.length > 0) {
			const brand = getBrandProductData.find(
				item => item.slug === brandSlug,
			);
			if (brand) {
				setBrandDataAll(brand.id);
			}
		}

		if (categoryParam && getCategoryData.length > 0) {
			const category = getCategoryData.find(
				item => item.product_data?.slug === categoryParam,
			);
			if (category) {
				setTypeDataAll(category?.product_data?.id);
			}
		}
	}, [searchParams, getBrandProductData, getCategoryData]);

	const productCategory = [
		{ value: 0, label: 'All Categories' },
		...getCategoryData.map(item => ({
			value: item.product_data?.id,
			label: reactHtmlParser(item.product_data?.title),
			slug: item?.product_data?.slug,
		})),
	];

	const productBrandOption = [
		{ value: 0, label: 'All Brand' },
		...sortedBrandProductData.map(item => ({
			value: item.id,
			label: reactHtmlParser(item.title),
			slug: item?.slug,
		})),
	];

	const updateURL = (brandId, categoryId) => {
		const params = new URLSearchParams();

		if (brandId && brandId !== 0) {
			const brand = getBrandProductData.find(item => item.id === brandId);
			if (brand?.slug) {
				params.set('brand', brand.slug);
			}
		}

		if (categoryId && categoryId !== 0) {
			const category = getCategoryData.find(
				item => item.product_data?.id === categoryId,
			);
			if (category?.product_data?.slug) {
				params.set('category', category.product_data.slug);
			}
		}

		const newURL = params.toString()
			? `${pathName}?${params.toString()}`
			: pathName;
		router.replace(newURL, { scroll: false });
	};

	const handleClickType = index => {
		setTypeDataAll(index?.value);
		updateURL(getBrandDataAll, index?.value);
		// If "All Categories" is selected, show all products. Otherwise, show products whose parent_product_id matches the selected category.
		if (index?.value === 0) {
			setProductData(productData);
		} else {
			const filtered = productData.filter(
				item => item?.product_data?.parent_product_id === index?.value,
			);
			setProductData(filtered);
		}
		setTimeout(() => {
			ScrollTrigger.refresh();
		}, 100);
	};

	const handleClickBrand = index => {
		setBrandDataAll(index?.value);
		updateURL(index?.value, getTypeDataAll);
		setTimeout(() => {
			ScrollTrigger.refresh();
		}, 100);
	};

	const handleClearAll = () => {
		setTypeDataAll(0);
		setBrandDataAll(0);
		// Remove only query params, keep the current path
		const url = window.location.pathname;
		router.replace(url, { scroll: false });
		setTimeout(() => {
			ScrollTrigger.refresh();
		}, 100);
	};


	return (
		<StyledStockList className="JobList" data-scroll-section>
			<Container>
				<Row className={'filter'}>
					<Col md={3}>
						<SelectInput
							onChange={handleClickType}
							options={productCategory}
							placeholder={'All Categories'}
							selectedValue={getTypeDataAll}
						/>
					</Col>
					<Col md={3}>
						<SelectInput
							onChange={handleClickBrand}
							options={productBrandOption}
							placeholder={'All Brands'}
							selectedValue={getBrandDataAll}
						/>
					</Col>
					<Col md={3}>
						{/*<SelectInput placeholder={'Filter 3'} />*/}
					</Col>
					<Col
						md={3}
						className={
							'd-flex justify-content-end align-items-center'
						}>
						{/* <LinkOnly text={'Clear All'} color={'#DD223F'} onClick={handleClearAll} /> */}
					</Col>
				</Row>
				<Row className={'stock-list'}>
					{(() => {
						const filteredProducts = getProductData?.filter(
							subItem => {
								const productBrand =
									subItem?.product_data?.product_brand_id;
								const parentProductId =
									subItem?.product_data?.parent_product_id;

								if (!parentProductId || !productBrand)
									return false; // skip 0/null/undefined

								// If a category is selected, filter where parent_product_id === selected category id
								const categoryMatch =
									getTypeDataAll && getTypeDataAll !== 0
										? parentProductId === getTypeDataAll
										: true;
								const brandMatch = getBrandDataAll
									? getBrandDataAll === productBrand
									: true;

								return categoryMatch && brandMatch;
							},
						);

						if (
							!filteredProducts ||
							filteredProducts.length === 0
						) {
							return (
								<Col md={12} className="text-center py-5">
									No products found for the selected filters.
								</Col>
							);
						}

						return filteredProducts.map((subItem, subIndex) => {
							const thumb = subItem?.images?.list?.find(
								f => f?.thumb === 'on',
							);
							return (
								<Col
									md={12}
									key={subIndex}
									className="single-item">
									<StockSingle
										link={`/product/category/${subItem?.product_data?.slug}`}
										des={subItem?.product_data?.short_desc}
										title={subItem?.product_data?.title}
										img={thumb?.full_path}
									/>
								</Col>
							);
						});
					})()}
				</Row>
			</Container>
		</StyledStockList>
	);
};

export default React.memo(StockList);
